import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, Copy, FilePen, Plus, Trash2 } from 'lucide-react';
import type { UpstreamConfig } from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import { usePricing } from '../api/pricing.ts';
import { useDeleteUpstream, useUpstreamCatalog, useUpstreamsList } from '../api/useUpstreams.ts';
import { formatUsdCentsAsCnyBalance } from '../components/formatters.ts';
import { StatusChip } from '../components/StatusChip.tsx';
import { useToast, coerceErrorMessage } from '../components/Toast.tsx';
import { useClipboard } from '../hooks/useClipboard.tsx';
import { useT } from '../i18n/index.ts';
import {
  Button,
  Dialog,
  IconButton,
  Input,
  KpiCard,
  PageHeader,
  Table,
  TBody,
  Td,
  Th,
  THead,
  Tooltip,
  Tr,
  EmptyState,
} from '../ui/index.ts';

/**
 * `/supply/upstreams` list view — Dispatch Deck (epic #835).
 * Stats row (KpiCard ×4), dense hairline table (no zebra), always-visible
 * row actions, Dialog-backed soft-delete confirm.
 */

function upstreamVariant(row: UpstreamConfig): 'ok' | 'warning' | 'neutral' {
  if (!row.enabled) {
    return 'warning';
  }
  return 'ok';
}

function formatDiagnosticSource(no: number): string {
  return `S${Math.max(0, Math.floor(no)).toString().padStart(3, '0')}`;
}

/* ── Stats row ──────────────────────────────────────────────────────── */

interface StatsRowProps {
  readonly total: number;
  readonly enabled: number;
  readonly accounts: string;
  readonly funding: string;
}

function StatsRow({ total, enabled, accounts, funding }: StatsRowProps): JSX.Element {
  const t = useT();
  return (
    <div data-testid="upstreams-stats" className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <KpiCard label={t('upstreams.stats.total')} value={total} />
      <KpiCard label={t('upstreams.stats.enabled')} value={enabled} lamp="ok" />
      <KpiCard label={t('upstreams.stats.accounts')} value={accounts} />
      <KpiCard label={t('upstreams.stats.funding')} value={funding} />
    </div>
  );
}

/* ── Endpoint copy cell ──────────────────────────────────────────────── */

function EndpointCopyCell({
  upstreamId,
  endpointUrl,
}: {
  readonly upstreamId: string;
  readonly endpointUrl: string;
}): JSX.Element {
  const { copy, copied } = useClipboard();
  const t = useT();
  return (
    <Tooltip content={<span className="font-mono">{endpointUrl}</span>}>
      <button
        type="button"
        onClick={() => void copy(endpointUrl)}
        aria-label={t('upstreams.row.copy_endpoint')}
        data-testid={`upstream-row-${upstreamId}-endpoint-copy`}
        className="inline-flex max-w-[240px] items-center gap-2 rounded border border-line bg-well px-2 py-1 text-left font-mono text-dense text-paper-mid transition-colors duration-120 hover:border-line-strong hover:text-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40"
      >
        <code className="min-w-0 truncate">{endpointUrl}</code>
        {copied ? (
          <Check aria-hidden="true" className="h-3.5 w-3.5 shrink-0 text-ok" />
        ) : (
          <Copy aria-hidden="true" className="h-3.5 w-3.5 shrink-0 text-paper-low" />
        )}
      </button>
    </Tooltip>
  );
}

/* ── Table row ──────────────────────────────────────────────────────── */

interface RowProps {
  readonly row: UpstreamConfig;
  readonly modelCount: number;
  readonly onDelete: (row: UpstreamConfig) => void;
  readonly deleting: boolean;
  readonly statusLabel: string;
  readonly cnyPerUsd: number | null;
}

function UpstreamRow({
  row,
  modelCount,
  onDelete,
  deleting,
  statusLabel,
  cnyPerUsd,
}: RowProps): JSX.Element {
  const t = useT();
  return (
    <Tr data-testid={`upstream-row-${row.id}`}>
      {/* Name */}
      <Td emphasis>
        <Link
          to={`/supply/upstreams/${row.id}`}
          className="text-signal hover:underline focus-visible:underline focus-visible:outline-none"
        >
          {row.name}
        </Link>
      </Td>

      {/* Endpoint */}
      <Td>
        <EndpointCopyCell upstreamId={row.id} endpointUrl={row.endpoint_url} />
      </Td>

      {/* Support source */}
      <Td mono>{formatDiagnosticSource(row.diagnostic_source_no)}</Td>

      {/* Status */}
      <Td>
        <StatusChip
          variant={upstreamVariant(row)}
          label={statusLabel}
          testId={`upstream-row-${row.id}-status`}
        />
      </Td>

      {/* Accounts */}
      <Td num data-testid={`upstream-row-${row.id}-accounts`}>
        {typeof row.account_count === 'number' ? row.account_count : '—'}
      </Td>

      {/* Mappings — model count from catalog */}
      <Td num data-testid={`upstream-row-${row.id}-models`}>
        {modelCount}
      </Td>

      {/* Funding */}
      <Td num emphasis data-testid={`upstream-row-${row.id}-funding`}>
        {typeof row.active_funding_remaining_usd_cents === 'number'
          ? formatUsdCentsAsCnyBalance(row.active_funding_remaining_usd_cents, cnyPerUsd)
          : '—'}
      </Td>

      {/* Actions — always visible (hover-opacity reveals are banned) */}
      <Td>
        <div className="flex justify-end gap-1">
          <Link
            to={`/supply/upstreams/${row.id}`}
            data-testid={`upstream-row-${row.id}-edit`}
            aria-label={t('upstreams.row.edit')}
            title={t('upstreams.row.edit')}
            className="inline-flex h-7 w-7 items-center justify-center rounded text-paper-mid transition-colors duration-120 hover:bg-raised hover:text-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40"
          >
            <FilePen aria-hidden="true" className="h-4 w-4" />
          </Link>
          <IconButton
            label={t('upstreams.row.delete')}
            size="sm"
            onClick={() => onDelete(row)}
            disabled={deleting}
            data-testid={`upstream-row-${row.id}-delete`}
            className="hover:bg-bad-dim hover:text-bad"
          >
            <Trash2 aria-hidden="true" className="h-4 w-4" />
          </IconButton>
        </div>
      </Td>
    </Tr>
  );
}

/* ── Delete confirm modal ──────────────────────────────────────────── */

interface DeleteTarget {
  readonly id: string;
  readonly name: string;
}

interface DeleteConfirmProps {
  readonly name: string;
  readonly busy: boolean;
  readonly error: string | null;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}

function DeleteConfirmModal({
  name,
  busy,
  error,
  onCancel,
  onConfirm,
}: DeleteConfirmProps): JSX.Element {
  const t = useT();
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !busy) {
          onCancel();
        }
      }}
      title={t('upstreams.delete_modal.title')}
      size="sm"
      closeLabel={t('common.close')}
      testId="delete-confirm"
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="danger"
            onClick={onConfirm}
            disabled={busy}
            loading={busy}
            data-testid="delete-confirm-submit"
          >
            {busy ? t('common.deleting') : t('common.delete')}
          </Button>
        </>
      }
    >
      <p className="m-0 text-body text-paper-mid">
        <span className="font-medium text-paper">{name}</span>
        {t('upstreams.delete_modal.body_suffix')}
      </p>
      {error !== null ? (
        <p className="mt-3 text-label text-bad" role="alert" data-testid="delete-confirm-error">
          {error}
        </p>
      ) : null}
    </Dialog>
  );
}

/* ── Page component ─────────────────────────────────────────────────── */

export function UpstreamsList(): JSX.Element {
  const list = useUpstreamsList();
  const catalog = useUpstreamCatalog();
  const pricing = usePricing();
  const cnyPerUsd = pricing.data?.policy?.cny_per_usd ?? null;
  const toast = useToast();
  const t = useT();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [deleteMutationId, setDeleteMutationId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const deleteMutation = useDeleteUpstream(deleteMutationId ?? '');

  const modelCounts = useMemo(() => {
    const counts = new Map<string, number>();
    const entries = catalog.data?.entries ?? [];
    for (const entry of entries) {
      counts.set(entry.upstream.id, entry.models.length);
    }
    return counts;
  }, [catalog.data]);

  const filtered = useMemo(() => {
    const rows = list.data ?? [];
    const query = search.trim().toLowerCase();
    if (query.length === 0) {
      return rows;
    }
    return rows.filter(
      (row) =>
        row.name.toLowerCase().includes(query) ||
        row.endpoint_url.toLowerCase().includes(query) ||
        row.kind.toLowerCase().includes(query),
    );
  }, [list.data, search]);

  /** Compute stats from the full (unfiltered) list. */
  const stats = useMemo(() => {
    const rows = list.data ?? [];
    const total = rows.length;
    const enabled = rows.filter((r) => r.enabled).length;
    const hasAccountSummary = rows.every((r) => typeof r.account_count === 'number');
    const hasFundingSummary = rows.every(
      (r) => typeof r.active_funding_remaining_usd_cents === 'number',
    );
    const accounts = hasAccountSummary
      ? rows.reduce((sum, row) => sum + (row.account_count ?? 0), 0).toString()
      : '—';
    const activeFundingCents = hasFundingSummary
      ? rows.reduce((sum, row) => sum + (row.active_funding_remaining_usd_cents ?? 0), 0)
      : null;
    return {
      total,
      enabled,
      accounts,
      funding:
        activeFundingCents === null
          ? '—'
          : formatUsdCentsAsCnyBalance(activeFundingCents, cnyPerUsd),
    };
  }, [cnyPerUsd, list.data]);

  async function confirmDelete(): Promise<void> {
    if (deleteTarget === null) {
      return;
    }
    setDeleteMutationId(deleteTarget.id);
    setDeleteError(null);
    try {
      await deleteMutation.mutateAsync();
      setDeleteTarget(null);
      setDeleteMutationId(null);
    } catch (err) {
      const message =
        err instanceof OpsApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err);
      setDeleteError(message);
      toast.error(message);
    }
  }

  return (
    <section data-testid="upstreams-list-page" className="flex flex-col gap-6">
      <PageHeader
        title={t('upstreams.title')}
        sub={t('upstreams.subtitle')}
        actions={
          <>
            <Input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              placeholder={t('upstreams.search.placeholder')}
              aria-label={t('upstreams.search.aria_label')}
              data-testid="upstreams-search"
              className="h-8 w-56"
            />
            <Button
              variant="primary"
              data-testid="upstreams-new"
              onClick={() => navigate('/supply/upstreams/new')}
              title={t('upstreams.new.title')}
            >
              <Plus aria-hidden="true" className="h-4 w-4" />
              {t('upstreams.new')}
            </Button>
          </>
        }
      />

      {list.isError ? (
        <div
          role="alert"
          data-testid="upstreams-error"
          className="flex items-center justify-between gap-4 rounded-lg border border-bad-line bg-bad-dim px-4 py-3"
        >
          <div className="flex flex-col gap-1">
            <span className="text-body font-medium text-bad-bright">
              {t('upstreams.error.title')}
            </span>
            <span className="text-dense text-bad-bright/80">
              {list.error instanceof OpsApiError
                ? `${list.error.code}: ${list.error.message}`
                : t('common.network_error')}
            </span>
          </div>
          <Button size="sm" onClick={() => void list.refetch()} data-testid="upstreams-retry">
            {t('common.retry')}
          </Button>
        </div>
      ) : null}

      {list.isPending ? (
        <div
          data-testid="upstreams-loading"
          className="flex flex-col gap-2 rounded-lg border border-line-soft bg-card p-4"
        >
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-12 animate-pulse rounded bg-raised" aria-hidden="true" />
          ))}
          <span className="sr-only">{t('upstreams.loading_aria')}</span>
        </div>
      ) : null}

      {list.isSuccess && filtered.length === 0 ? (
        <div data-testid="upstreams-empty" className="rounded-lg border border-line-soft bg-card">
          <EmptyState
            title={t('upstreams.empty.title')}
            hint={
              search.length > 0
                ? t('upstreams.empty.search_body')
                : t('upstreams.empty.initial_body')
            }
          />
        </div>
      ) : null}

      {list.isSuccess && filtered.length > 0 ? (
        <>
          {/* Stats row */}
          <StatsRow
            total={stats.total}
            enabled={stats.enabled}
            accounts={stats.accounts}
            funding={stats.funding}
          />

          {/* Data table */}
          <Table testId="upstreams-table">
            <THead>
              <tr>
                <Th>{t('upstreams.column.name')}</Th>
                <Th>{t('upstreams.column.endpoint')}</Th>
                <Th>{t('upstreams.column.source')}</Th>
                <Th>{t('upstreams.column.status')}</Th>
                <Th num>{t('upstreams.column.accounts')}</Th>
                {/* Catalog model count from modelCounts above. */}
                <Th num>{t('upstreams.column.models')}</Th>
                <Th num>{t('upstreams.column.funding')}</Th>
                <Th className="text-right">{t('upstreams.column.actions')}</Th>
              </tr>
            </THead>
            <TBody>
              {filtered.map((row) => (
                <UpstreamRow
                  key={row.id}
                  row={row}
                  modelCount={modelCounts.get(row.id) ?? 0}
                  onDelete={(target) => {
                    setDeleteError(null);
                    setDeleteTarget({ id: target.id, name: target.name });
                  }}
                  deleting={deleteMutationId === row.id && deleteMutation.isPending}
                  cnyPerUsd={cnyPerUsd}
                  statusLabel={
                    row.enabled ? t('upstreams.status.enabled') : t('upstreams.status.disabled')
                  }
                />
              ))}
            </TBody>
          </Table>
        </>
      ) : null}

      {deleteTarget !== null ? (
        <DeleteConfirmModal
          name={deleteTarget.name}
          busy={deleteMutation.isPending}
          error={deleteError}
          onCancel={() => {
            if (!deleteMutation.isPending) {
              setDeleteTarget(null);
              setDeleteMutationId(null);
              setDeleteError(null);
            }
          }}
          onConfirm={() => void confirmDelete()}
        />
      ) : null}
    </section>
  );
}
